import { CellContext } from '@tanstack/react-table'

import CharacterAvatar from '../../components/CharacterAvatar'
import { Link } from 'react-router-dom'
import { genericSorting } from './util'
import Timestamp from '../../components/Timestamp'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Edit } from 'lucide-react'
import { TFunction } from 'i18next'
import TeamMemberContextMenu from '../../pages/TeamDashboard/subpages/TeamRoster/components/TeamMemberContextMenu'
import UserBnetConnection from '../../pages/TeamDashboard/subpages/TeamRoster/components/UserBnetConnection'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import {
  NoteCell,
  EditableRankCell,
  EditableRoleCell,
  EditableClassCell,
  EditableOffSpecsCell,
} from '../../pages/TeamDashboard/subpages/TeamRoster/components/EditableRosterCells'

export const name = {
  accessorKey: '0.characterReference.name',
  id: 'name',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Name'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const [main, ...alts] = characters

    const isOrphanAlt = main.teamRank.player_type === 'alt'

    return (
      <div className='flex flex-col gap-1'>
        <Link
          to={main.characterReference.path}
          className={`inline-flex gap-2 items-center ${
            isOrphanAlt ? 'text-muted-foreground text-xs' : ''
          }`}
        >
          <CharacterAvatar
            character={main.characterReference}
            className={'h-6 w-6'}
          />
          <span
            className={
              classColours[main.characterReference.characterClass].text
            }
          >
            {main.characterReference.name}
          </span>
        </Link>

        {alts.map((alt) => (
          <div key={alt.id} className='h-6 text-xs'>
            <Link
              to={alt.characterReference.path}
              className='inline-flex gap-2 items-center ml-1 text-muted-foreground'
            >
              <span className='text-muted-foreground text-sm mr-2'>└</span>
              <CharacterAvatar
                character={alt.characterReference}
                className='h-4 w-4'
              />
              <span
                className={
                  classColours[alt.characterReference.characterClass].text
                }
              >
                {alt.characterReference.name}
              </span>
            </Link>
          </div>
        ))}
      </div>
    )
  },
}

export const refreshedAt = (
  t: TFunction,
  action?: (member: wowaudit.TeamMembership) => JSX.Element,
) => {
  return {
    accessorKey: '0.characterReference.refreshedAt',
    id: 'refreshedAt',
    ...genericSorting<wowaudit.TeamMembership[]>(
      'alphanumeric',
      <div className='text-right'>Details updated</div>,
    ),
    cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
      const characters = info.row.original
      const isOrphanAlt = characters[0].teamRank.player_type === 'alt'

      return (
        <div className='flex flex-col gap-1'>
          {characters.map((member) => (
            <div
              key={member.id}
              className={`text-right h-6 text-xs ${
                isOrphanAlt ? '' : 'first:text-sm'
              } flex items-center justify-end`}
            >
              {member.characterReference.gdprStatus === 'exists' ? (
                <Timestamp
                  time={member.characterReference.refreshedAt}
                  neverText={member.characterReference.status.replace('_', ' ')}
                />
              ) : (
                <div className='flex items-center justify-end gap-2'>
                  <Popover>
                    <PopoverTrigger asChild className='flex items-center gap-2'>
                      <Edit
                        size={14}
                        className='cursor-pointer opacity-70 hover:opacity-100'
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      align='end'
                      className='text-sm text-center w-36'
                    >
                      <div className='mb-2'>{t('shared.mark-as')}</div>
                      <div className='flex flex-col gap-1'>
                        {action ? action(member) : null}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <span className='italic text-destructive-foreground'>
                    {t(
                      `profile.character-gdpr-status.${member.characterReference.gdprStatus}`,
                    ).toLowerCase()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )
    },
  }
}

export const role = {
  accessorKey: '0.role',
  id: 'role',
  size: 110,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Role'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <EditableRoleCell key={member.id} member={member} index={i} />
        ))}
      </div>
    )
  },
}

export const note = {
  accessorKey: '0.note',
  id: 'note',
  size: 180,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Note'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <NoteCell key={member.id} member={member} index={i} />
        ))}
      </div>
    )
  },
}

export const itemLevel = (
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
) => {
  return {
    accessorFn: (row: wowaudit.TeamMembership[]) => {
      const details = extendedDetails[row[0].characterReference.id]
      return details?.maxItemLevel || 0
    },
    id: 'itemLevel',
    size: 75,
    ...genericSorting<wowaudit.TeamMembership[]>('alphanumeric', 'iLvl', true),
    cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
      const characters = info.row.original
      const isOrphanAlt = characters[0].teamRank.player_type === 'alt'

      return (
        <div className='flex flex-col gap-1'>
          {characters.map((member) => {
            const details = extendedDetails[member.characterReference.id]
            return (
              <div
                key={member.id}
                className={`transition-opacity duration-300 w-8 h-6 flex items-center text-xs text-muted-foreground ${
                  isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
                } ${details ? 'opacity-100' : 'opacity-0'}`}
              >
                {details?.maxItemLevel || '-'}
              </div>
            )
          })}
        </div>
      )
    },
  }
}

export const level = {
  accessorFn: (row: wowaudit.TeamMembership[]) =>
    row[0].characterReference.level || 0,
  id: 'level',
  size: 75,
  ...genericSorting<wowaudit.TeamMembership[]>('alphanumeric', 'Level', true),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const isOrphanAlt = characters[0].teamRank.player_type === 'alt'

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member) => (
          <div
            key={member.id}
            className={`w-8 h-6 flex items-center text-xs text-muted-foreground ${
              isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
            }`}
          >
            {member.characterReference.level || '-'}
          </div>
        ))}
      </div>
    )
  },
}

export const rank = {
  accessorFn: (row: wowaudit.TeamMembership[]) => row[0].teamRank.name ?? '',
  id: 'rank',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Rank'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <EditableRankCell key={member.id} member={member} index={i} />
        ))}
      </div>
    )
  },
}

export const spec = {
  accessorKey: '0.characterReference.characterClass',
  id: 'characterClass',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Class'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <EditableClassCell key={member.id} member={member} index={i} />
        ))}
      </div>
    )
  },
}

export const offSpecs = {
  accessorFn: (row: wowaudit.TeamMembership[]) =>
    row[0].offspecDetails.map((s) => s.name).join(', '),
  id: 'offspecDetails',
  size: 100,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Off specs'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <EditableOffSpecsCell key={member.id} member={member} index={i} />
        ))}
      </div>
    )
  },
}

export const connections = (
  usersById: Record<string, wowaudit.UserReference> | undefined,
  userIdsWithDuplicateMains: number[],
) => ({
  accessorKey: '0.connections',
  id: 'connections',
  size: 90,
  ...genericSorting<wowaudit.TeamMembership[]>('text', ''),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, index) => (
          <div
            key={member.id}
            className={`h-6 flex items-start ${
              usersById === undefined ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {index === 0 ? (
              <UserBnetConnection
                member={characters[0]}
                usersById={usersById}
                userIdsWithDuplicateMains={userIdsWithDuplicateMains}
              />
            ) : null}
          </div>
        ))}
      </div>
    )
  },
})
export const actions = {
  id: 'actions',
  size: 50,
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member) => (
          <div key={member.id} className='h-6 flex items-center justify-center'>
            <TeamMemberContextMenu member={member} />
          </div>
        ))}
      </div>
    )
  },
}
